export type AudioProgressCallback = (
  positionMs: number,
  completed: boolean
) => void

export interface AudioProgressReporter {
  /** Call on every `timeupdate` — forwards to `onProgress` at most once per
   * `intervalMs`, so a 250ms-firing browser event doesn't flood the server. */
  report(positionMs: number): void
  /** Call on `pause`/`ended` — always forwards immediately, and resets the
   * throttle window so the next `report()` waits a full interval again. */
  flush(positionMs: number, completed: boolean): void
}

const DEFAULT_INTERVAL_MS = 10_000

/**
 * `onProgress` is throttled client-side rather than server-side: the player
 * has no reason to trust the backend to de-duplicate a `timeupdate`-driven
 * flood, and a pure, clock-injectable reporter is what makes the throttle
 * itself testable without a real `<audio>` element.
 */
export const createAudioProgressReporter = (
  onProgress: AudioProgressCallback,
  intervalMs: number = DEFAULT_INTERVAL_MS,
  now: () => number = () => Date.now()
): AudioProgressReporter => {
  let lastReportedAt: number | null = null

  const report = (positionMs: number) => {
    const currentTime = now()
    if (lastReportedAt !== null && currentTime - lastReportedAt < intervalMs)
      return
    lastReportedAt = currentTime
    onProgress(positionMs, false)
  }

  const flush = (positionMs: number, completed: boolean) => {
    lastReportedAt = now()
    onProgress(positionMs, completed)
  }

  return { report, flush }
}
